import { Button as MaterialButton, ButtonProps as MuiButtonProps, CircularProgress } from "@mui/material"
import React, { forwardRef, MouseEventHandler, ReactElement, ReactNode } from "react"
import styled, { css } from "styled-components"
import { theme } from "../../../theme"
import { Tooltip } from "../../common/Tooltip"
import { ArrowDropDown } from "@mui/icons-material"

export enum ButtonVariant {
  outlined = "outlined",
  primary = "primary",
  text = "text",
  area = "area",
}

export enum ButtonSize {
  small = "small",
  medium = "medium",
}

type PropsFromMui =
  | "color"
  | "ref"
  | "id"
  | "value"
  | "type"
  | "onMouseDown"
  | "autoFocus"
  | "onMouseEnter"
  | "onMouseOver"
  | "onMouseLeave"

export type ButtonProps = Pick<MuiButtonProps, PropsFromMui> & {
  disabled?: boolean
  loading?: boolean
  minimalArea?: boolean
  ripples?: boolean
  dark?: boolean
  onClick?: MouseEventHandler<HTMLButtonElement>
  variant?: ButtonVariant
  rounded?: boolean
  fontFamily?: "regular" | "bold"
  href?: string
  icon?: ReactElement
  children?: ReactNode
  tooltip?: string
  dropdown?: boolean
  size?: ButtonSize
  "data-cy"?: string
  noBackground?: boolean
  id?: string
}

export const ButtonStyle = styled(MaterialButton).attrs((props: ButtonProps) => ({
  color: props.color,
  variant: props.variant,
  ...props,
}))`
  box-shadow: ${theme.mui.shadows[1]};
  border-radius: 0;
`
const iconButton = css`
  min-width: 0;
  min-height: 0;
  padding: 0;
  width: ${theme.buttonSize.m};
  height: ${theme.buttonSize.m};
  .MuiButton-startIcon {
    margin: 0;
  }
`

const noRipple = css`
  .MuiTouchRipple-root {
    display: none;
  }
`

const fontFamilies = {
  bold: css`
    font-family: ${theme.font.bold};
  `,
  regular: css`
    font-family: ${theme.font.regular};
  `,
}

const noBordersCss = css`
  border: none;
  box-shadow: none;

  &:hover {
    border: none;
  }
`

const area = css`
  box-shadow: none;
  display: flex;
  flex-grow: inherit;
  align-items: inherit;
  justify-content: inherit;
  padding: 0;
  margin: 0;
  color: inherit;
  text-transform: inherit;
  font-weight: inherit;
  text-align: inherit;
  line-height: inherit;
  font-size: inherit;
  font-family: inherit;
  overflow: hidden;

  > span:first-child {
    height: 100%;
  }
`

const minimalAreaStyle = css`
  background-color: unset;
  min-width: unset;
`

const noBackgroundStyle = css`
  background-color: unset;
  min-width: unset;

  :disabled {
    background-color: unset !important;
  }
`

const roundedStyle = css`
  border-radius: 20px;
`

const loadingStyle = {
  hasIcon: css`
    .MuiButton-startIcon {
      visibility: visible;
    }
  `,
  hasNoIcon: css`
    .MuiButton-startIcon {
      visibility: visible;
      position: absolute;
    }
  `,
}

const getLightStyle = (backgroundColorHover: string, color: string) => {
  return css`
    box-shadow: none;
    &:hover {
      background: ${backgroundColorHover};
      box-shadow: none;
    }

    &:visited {
      color: ${color};
    }
  `
}

const lightsStyle = {
  primary: getLightStyle(theme.color.primaryDark, theme.color.white),
  outlined: getLightStyle(theme.color.primary50, theme.color.primary),
  text: getLightStyle(theme.color.primary50, theme.color.primary),
  area: css``,
}

const getDarkStyle = (color: string, backgroundColor: string, backgroundColorHover: string, hasBorder?: boolean) => {
  return css`
    color: ${color};
    background: ${backgroundColor};
    ${hasBorder && `border: 1px solid ${theme.color.white1};`}
    box-shadow: none;

    &:hover {
      background: ${backgroundColorHover};
      ${hasBorder && `border: 1px solid ${theme.color.white1};`}
      box-shadow: none;
    }
  `
}

const darksStyle = {
  primary: getDarkStyle(theme.color.black1, theme.color.white, theme.color.grey5),
  outlined: getDarkStyle(theme.color.white1, "transparent", "rgba(255, 255, 255, 0.2)", true),
  text: getDarkStyle(theme.color.white1, "transparent", "rgba(255, 255, 255, 0.1)"),
  area: css``,
}

const ButtonSpinner = styled(CircularProgress)`
  color: ${theme.color.grey2};
`

const ButtonFactory = styled(ButtonStyle)<{
  $hasRipples?: boolean
  $fontFamily?: keyof typeof fontFamilies
  $variant?: ButtonVariant
  size?: ButtonSize
  $dark?: boolean
  $minimalArea?: boolean
  $loading?: boolean
  $hasIcon?: boolean
  $rounded?: boolean
  $noBackground?: boolean
}>`
  ${(p) => (p.$hasIcon && !p.children ? iconButton : "")}
  ${(p) => (!p.$hasRipples ? noRipple : "")}
  ${(p) => (p.$fontFamily ? fontFamilies[p.$fontFamily] : "")}
  ${(p) => (p.$variant === ButtonVariant.text ? noBordersCss : "")}
  ${(p) => (p.$variant === ButtonVariant.area ? area : "")}
  ${(p) => p.$variant && (p.$dark ? darksStyle[p.$variant] : lightsStyle[p.$variant])}
  ${(p) => (p.$loading && p.$hasIcon ? loadingStyle.hasIcon : "")}
  ${(p) => (p.$loading && !p.$hasIcon ? loadingStyle.hasNoIcon : "")}
  ${(p) => (p.size === ButtonSize.medium ? `min-height: ${theme.buttonSize.m};` : "")}
  ${(p) => (p.$rounded ? roundedStyle : "")}

  && {
    ${(p) => (p.$noBackground ? noBackgroundStyle : "")}
    ${(p) => (p.$minimalArea ? minimalAreaStyle : "")}
  }

  .MuiButton-startIcon {
    margin-right: ${(p) => (!p.children || !p.$hasIcon ? 0 : theme.spacing.m)};
    margin-left: 0;
  }

  &:disabled {
    color: ${(p) => (p.$loading ? (p.$dark ? theme.color.grey1 : theme.color.grey5) : theme.color.grey2)} !important;
    background-color: ${(p) => (p.$dark ? theme.color.grey1 : theme.color.grey5)} !important;
    ${noBordersCss}
  }
`

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    disabled = false,
    loading = false,
    minimalArea,
    ripples = true,
    noBackground = false,
    dark,
    onClick,
    icon,
    children,
    tooltip = "",
    variant,
    fontFamily,
    rounded,
    dropdown,
    size = ButtonSize.medium,
    ...rest
  } = props

  const shouldDisable = disabled || loading
  const isMinimalArea = variant === ButtonVariant.area ? minimalArea : false
  const hasNoBackground = variant === ButtonVariant.text && !children ? noBackground : false
  const hasRipples = hasNoBackground ? false : ripples

  const getIconContent = () => {
    if (loading) return <ButtonSpinner size={24} />

    return icon
  }

  const getDropdownIcon = () => {
    if (dropdown) return <ArrowDropDown />
  }

  const getButtonContent = () => {
    return !!children && children
  }

  const getMuiVariant = (buttonVariant?: ButtonVariant) => {
    if (buttonVariant === ButtonVariant.primary) return "contained"
    if (buttonVariant === ButtonVariant.area) return undefined

    return "outlined"
  }

  const getMuiColor = (buttonVariant?: ButtonVariant) => {
    if (buttonVariant === ButtonVariant.area) return undefined

    return "primary"
  }

  const commonProps = {
    ref,
    onClick,
    size,
    disabled: shouldDisable,
    startIcon: getIconContent(),
    endIcon: getDropdownIcon(),
    variant: getMuiVariant(variant),
    color: getMuiColor(variant),
    children: getButtonContent(),
    $minimalArea: isMinimalArea,
    $hasRipples: hasRipples,
    $fontFamily: fontFamily,
    $variant: variant,
    $dark: dark,
    $loading: loading,
    $hasIcon: !!icon,
    $rounded: rounded,
    $noBackground: hasNoBackground,
    ...rest,
  }

  return (
    <Tooltip title={tooltip} disableHoverListener={!tooltip}>
      <ButtonFactory {...commonProps} />
    </Tooltip>
  )
})

export const ButtonPrimary = (props: ButtonProps) => <Button {...props} variant={ButtonVariant.primary} />
export const ButtonOutlined = (props: ButtonProps) => <Button {...props} variant={ButtonVariant.outlined} />
export const ButtonText = (props: ButtonProps) => <Button {...props} variant={ButtonVariant.text} />
export const ButtonArea = (props: ButtonProps) => <Button {...props} variant={ButtonVariant.area} />
